import React, { useState, useEffect } from 'react';
import api from '../../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Table, Tabs, Pagination } from 'antd';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import '../Css/AnaliseComponente.css';
import {
  LineChartOutlined,
  BarChartOutlined,
  AreaChartOutlined,
  BulbOutlined,
  MenuOutlined
} from '@ant-design/icons';

const AnaliseMoldurasCC = () => {
  const navigate = useNavigate();

  const [repData, setRepData] = useState({});
  const [atrasoData, setAtrasoData] = useState({});
  const [frequeData, setFrequeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("1");

  const [currentRepPage, setCurrentRepPage] = useState(1);
  const [currentAtrasoPage, setCurrentAtrasoPage] = useState(1);

  // 🔹 Alerta de carregamento
  useEffect(() => {
    if (loading) {
      Swal.fire({
        title: 'Carregando...',
        text: 'Estamos processando as informações.',
        icon: 'info',
        showConfirmButton: false,
        willOpen: () => Swal.showLoading()
      });
    }
  }, [loading]);

  // 🔹 Carregar dados da API
  useEffect(() => {
    // Aba 1: Estatística Rep Molduras Externa
    api.get(`${process.env.REACT_APP_API_URL}/estatistica_atraso_rep_linhas_molduras_externa_cc`)
      .then(response => {
        setRepData(prev => ({ ...prev, estatistica_atraso_rep_externa: response.data.data || [] }));
      })
      .catch(() => {
        setRepData(prev => ({ ...prev, estatistica_atraso_rep_externa: [] }));
      });

    // Aba 1: Estatística Rep Molduras Interna
    api.get(`${process.env.REACT_APP_API_URL}/estatistica_atraso_rep_linhas_molduras_interna_cc`)
      .then(response => {
        setRepData(prev => ({ ...prev, estatistica_atraso_rep_interna: response.data.data || [] }));
        setLoading(false);
      })
      .catch(() => {
        setRepData(prev => ({ ...prev, estatistica_atraso_rep_interna: [] }));
        setLoading(false);
      });

    // Aba 2: Frequência Molduras
    api.get(`${process.env.REACT_APP_API_URL}/freque_molduras_cc`)
      .then(response => setFrequeData(response.data.data || []))
      .catch(() => setFrequeData([]));

    // Aba 3: Estatística Atraso Externa
    api.get(`${process.env.REACT_APP_API_URL}/estatistica_atraso_molduras_externa_cc`)
      .then(response => {
        setAtrasoData(prev => ({ ...prev, estatistica_atraso_externa: response.data.data || [] }));
      })
      .catch(() => {
        setAtrasoData(prev => ({ ...prev, estatistica_atraso_externa: [] }));
      });

    // Aba 3: Estatística Atraso Interna
    api.get(`${process.env.REACT_APP_API_URL}/estatistica_atraso_molduras_interna_cc`)
      .then(response => {
        setAtrasoData(prev => ({ ...prev, estatistica_atraso_interna: response.data.data || [] }));
      })
      .catch(() => {
        setAtrasoData(prev => ({ ...prev, estatistica_atraso_interna: [] }));
      });
  }, []);

  // 🔹 Fecha alerta
  useEffect(() => {
    if (!loading) {
      setTimeout(() => Swal.close(), 1500);
    }
  }, [loading]);

  const onTabChange = (key) => setActiveKey(key);

  // 🔹 Normalização da Aba 1 (Externa e Interna)
  const repExternaArray = (repData.estatistica_atraso_rep_externa || []).map(item => ({
    id: item.id,
    dezenas: item.externa,
    qte: item.qte,
    percente: item.percente,
    mdSorteio: item.mdSorteio,
    atrasados: item.atrasados,
    pj: item.pj
  }));

  const repInternaArray = (repData.estatistica_atraso_rep_interna || []).map(item => ({
    id: item.id,
    dezenas: item.interna,
    qte: item.qte,
    percente: item.percente,
    mdSorteio: item.mdSorteio,
    atrasados: item.atrasados,
    pj: item.pj
  }));

  const displayedRepData = currentRepPage === 1 ? repExternaArray : repInternaArray;

  // 🔹 Normalização da Aba 3 (Externa e Interna)
  const atrasoExternaArray = (atrasoData.estatistica_atraso_externa || []).map(item => ({
    id: item.id,
    dezenas: item.dezenas,
    qte: item.qte,
    percente: item.percente,
    mdSorteio: item.mdSorteio,
    atrasados: item.atrasados,
    pj: item.pj
  }));

  const atrasoInternaArray = (atrasoData.estatistica_atraso_interna || []).map(item => ({
    id: item.id,
    dezenas: item.dezenas,
    qte: item.qte,
    percente: item.percente,
    mdSorteio: item.mdSorteio,
    atrasados: item.atrasados,
    pj: item.pj
  }));

  const displayedAtrasoData = currentAtrasoPage === 1 ? atrasoExternaArray : atrasoInternaArray;

  // 🔹 Colunas
  const repColumns = [
    { title: 'Dezenas', dataIndex: 'dezenas', key: 'dezenas' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: t => `${t} %` },
    { title: 'Média Sorteio', dataIndex: 'mdSorteio', key: 'mdSorteio' },
    {
      title: 'Atrasados',
      dataIndex: 'atrasados',
      key: 'atrasados',
      render: (t, r) => (
        <span className={r.atrasados > r.mdSorteio ? 'highlight-high' : ''}>{t}</span>
      )
    },
    {
      title: 'Status Jogos',
      dataIndex: 'pj',
      key: 'pj',
      render: t => (
        <span className={t === 'Melhor Opção!' ? 'melhor-opcao' : ''}>{t}</span>
      )
    }
  ];

  const frequeColumns = [
    { title: 'Categoria', dataIndex: 'dezenas', key: 'dezenas' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: t => `${t} %` },
    { title: 'Frequência', dataIndex: 'freqAcu', key: 'freqAcu', render: t => `${t} - JG` },
    { title: 'Frequência AC', dataIndex: 'freqAcmlda', key: 'freqAcmlda', render: t => `${t} %` },
  ];

  const atrasoColumns = [
    { title: 'Números', dataIndex: 'dezenas', key: 'dezenas' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: t => `${t} %` },
    { title: 'Média Sorteio', dataIndex: 'mdSorteio', key: 'mdSorteio' },
    {
      title: 'Atrasados',
      dataIndex: 'atrasados',
      key: 'atrasados',
      render: (t, r) => (
        <span className={r.atrasados > r.mdSorteio ? 'highlight-high' : ''}>{t}</span>
      )
    },
    {
      title: 'Status Jogos',
      dataIndex: 'pj',
      key: 'pj',
      render: t => (
        <span className={t === 'Melhor Opção!' ? 'melhor-opcao' : ''}>{t}</span>
      )
    }
  ];

  // 🔹 Dados gráficos
  const repChartData = displayedRepData.map(item => ({
    name: `${item.dezenas} - Dz`,
    quantidade: item.qte,
    percentual: item.percente,
  }));

  const frequeChartData = frequeData.map(item => ({
    name: item.dezenas,
    quantidade: item.qte,
    percentual: item.percente,
    frequencia: item.freqAcu,
    frequenciaAc: item.freqAcmlda,
  }));

  const atrasoChartData = displayedAtrasoData.map(item => ({
    name: `Nu - ${item.dezenas}`,
    quantidade: item.qte,
    percentual: item.percente,
  }));

  // 🔹 Tabs
  const tabsItems = [
    {
      label: <LineChartOutlined className="icon-size-24" />,
      key: "1",
      children: (
        <div>
          <h2><span>Análise - Molduras</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={repChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
              <Bar dataKey="quantidade" barSize={50} fill="#007AA2" />
              <Line type="monotone" dataKey="percentual" stroke="#ff7300" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
            <Table columns={repColumns} dataSource={displayedRepData} loading={loading} rowKey="id" pagination={false} />
          </div>
          <Pagination
            current={currentRepPage}
            total={2}
            pageSize={1}
            onChange={setCurrentRepPage}
            style={{ marginTop: 16, textAlign: 'center' }}
            itemRender={(page, type, el) =>
              type === 'page' ? <a>{page === 1 ? 'Externa' : 'Interna'}</a> : el}
          />
        </div>
      )
    },
    {
      label: <BarChartOutlined className="icon-size-24" />,
      key: "2",
      children: (
        <div>
          <h2><span>Frequência - Molduras</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={frequeChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
              <Bar dataKey="quantidade" stackId="a" barSize={50} fill="#004B57" />
              <Bar dataKey="percentual" stackId="a" barSize={50} fill="#ff7300" />
              <Bar dataKey="frequencia" stackId="a" barSize={50} fill="#007AA2" />
              <Bar dataKey="frequenciaAc" stackId="a" barSize={50} fill="#008DC0" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
            <Table columns={frequeColumns} dataSource={frequeData} loading={loading} rowKey="id" pagination={false} />
          </div>
        </div>
      )
    },
    {
      label: <AreaChartOutlined className="icon-size-24" />,
      key: "3",
      children: (
        <div>
          <h2><span>Dezenas - Molduras</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={atrasoChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
              <Bar dataKey="quantidade" barSize={50} fill="#007AA2" />
              <Line type="monotone" dataKey="percentual" stroke="#ff7300" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
            <Table columns={atrasoColumns} dataSource={displayedAtrasoData} loading={loading} rowKey="id" pagination={false} />
          </div>
          <Pagination
            current={currentAtrasoPage}
            total={2}
            pageSize={1}
            onChange={setCurrentAtrasoPage}
            style={{ marginTop: 16, textAlign: 'center' }}
            itemRender={(page, type, el) =>
              type === 'page' ? <a>{page === 1 ? 'Externa' : 'Interna'}</a> : el}
          />
        </div>
      )
    },
    {
      label: <BulbOutlined className="icon-size-24" />,
      key: "4",
      children: <h2><span>Ideias Dz - Molduras</span></h2>
    },
    {
      label: <MenuOutlined className="icon-size-24" onClick={() => navigate("/dashboard")} />,
      key: "5"
    }
  ];

  return (
    <div className="analysis-container">
      <Tabs activeKey={activeKey} onChange={onTabChange} tabPosition="right" items={tabsItems} />
    </div>
  );
};

export default AnaliseMoldurasCC;
